import { messages, type Messages } from './catalog/index.ts';

type Join<P extends string, K extends string> = P extends '' ? K : `${P}.${K}`;

type LeafKeys<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? Join<P, K> : LeafKeys<T[K], Join<P, K>>;
}[keyof T & string];

type ValueAt<T, K extends string> = K extends `${infer Head}.${infer Rest}`
  ? Head extends keyof T
    ? ValueAt<T[Head], Rest>
    : never
  : K extends keyof T
    ? T[K]
    : never;

type Placeholders<S> = S extends `${string}{${infer Name}}${infer Rest}` ? Name | Placeholders<Rest> : never;

/** Katalogdaki her metnin anahtarı: `common.actions.cancel`, `errors.workforce.department.in_use`… */
export type CopyKey = LeafKeys<Messages>;

/** Metindeki `{ad}` yer tutucularından çıkarılan parametreler; yer tutucu yoksa parametre verilmez. */
export type CopyParams<K extends CopyKey> = [Placeholders<ValueAt<Messages, K>>] extends [never]
  ? []
  : [params: { readonly [P in Placeholders<ValueAt<Messages, K>>]: string | number }];

const numberFormat = new Intl.NumberFormat('tr-TR');

function lookup(key: string): string | undefined {
  let node: unknown = messages;
  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === null || !(part in node)) {
      return undefined;
    }

    node = (node as Record<string, unknown>)[part];
  }

  return typeof node === 'string' ? node : undefined;
}

function interpolate(template: string, params: Readonly<Record<string, string | number>>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    if (value === undefined) {
      return match;
    }

    // Sayılar binlik ayraçla yazılır (1.284); yıl gibi değerler metin olarak verilir.
    return typeof value === 'number' ? numberFormat.format(value) : value;
  });
}

/** Katalogdan metin. Anahtar ve parametreler derlemede denetlenir. */
export function t<K extends CopyKey>(key: K, ...args: CopyParams<K>): string {
  const template = lookup(key);
  if (template === undefined) {
    // Derlemede yakalanır; çalışma anında yalnız bozuk bir dinamik anahtar buraya düşer.
    return key;
  }

  const [params] = args as [Readonly<Record<string, string | number>>?];
  return params ? interpolate(template, params) : template;
}

/**
 * Anahtarı çalışma anında belli olan metin (ör. durum sözlüğünden gelen etiket anahtarı). Parametreler
 * derlemede denetlenmez; sabit anahtarlar için `t` kullanılır.
 */
export function translate(key: CopyKey, params?: Readonly<Record<string, string | number>>): string {
  const template = lookup(key);
  if (template === undefined) {
    return key;
  }

  return params ? interpolate(template, params) : template;
}

/** Çalışma anında gelen anahtar (ör. sunucudan gelen hata kodu) katalogda var mı? */
export function hasCopy(key: string): key is CopyKey {
  return lookup(key) !== undefined;
}

/**
 * RFC 9457 `code` alanının iletisi (08 §9.3): katalogda karşılığı varsa o, yoksa sunucunun Türkçe `detail`
 * iletisi, o da yoksa `title`, hiçbiri yoksa genel ileti (destek koduyla).
 */
export function errorMessage(
  code: string | null | undefined,
  detail?: string | null,
  params: Readonly<Record<string, string | number>> = {},
  title?: string | null,
): string {
  const key = code ? `errors.${code}` : undefined;
  const template = key ? lookup(key) : undefined;
  if (template !== undefined) {
    return interpolate(template, { supportCode: '—', ...params });
  }

  return detail?.trim() || title?.trim() || interpolate(messages.errors.internal, { supportCode: '—', ...params });
}

/** Sunucunun problem gövdesi (RFC 9457): kod, açıklama ve iletide kullanılan alanlar. */
export interface ProblemFields {
  readonly code?: string | null;
  readonly detail?: string | null;
  readonly title?: string | null;
  /** Hız sınırında bekleme (saniye); `Retry-After` başlığıyla aynı değer. */
  readonly retryAfterSeconds?: number | null;
  /** Hatalı kod ya da PIN denemesinden sonra kalan hak. */
  readonly remainingAttempts?: number | null;
  /** Kısa destek kodu (izleme kimliğinden, "4BF9-2F35"); bilinmeyen hatanın iletisinde gösterilir. */
  readonly supportCode?: string | null;
}

/**
 * Problem gövdesinin kullanıcı iletisi: `errorMessage` gibi, ama bekleme süresi, kalan hak ve destek kodu gövdedeki
 * alanlardan katalog metnine yerleşir ("Kod hatalı. 2 deneme hakkınız kaldı.").
 */
export function problemMessage(problem: ProblemFields): string {
  const params: Record<string, string | number> = {};
  if (typeof problem.retryAfterSeconds === 'number') {
    params.retryAfterSeconds = problem.retryAfterSeconds;
  }

  if (typeof problem.remainingAttempts === 'number') {
    params.remainingAttempts = problem.remainingAttempts;
  }

  if (problem.supportCode) {
    params.supportCode = problem.supportCode;
  }

  return errorMessage(problem.code, problem.detail, params, problem.title);
}

/** Ekran metninin anahtarları (`features/<özellik>/copy.ts`): `day.title`, `filters.issuesOnly`… */
export type ScreenCopyKey<M> = LeafKeys<M>;

/** Ekran metninin parametreleri: `{ad}` yer tutucularından; yer tutucu yoksa parametre verilmez. */
export type ScreenCopyParams<M, K extends string> = [Placeholders<ValueAt<M, K>>] extends [never]
  ? []
  : [params: { readonly [P in Placeholders<ValueAt<M, K>>]: string | number }];

/** Ekran metni işlevi: sabit anahtar derlemede denetlenir; `dynamic` çalışma anında belli olan anahtar içindir. */
export type ScreenCopy<M> = (<K extends LeafKeys<M>>(key: K, ...args: ScreenCopyParams<M, K>) => string) & {
  /** Çalışma anında gelen anahtar (ör. sunucunun kural anahtarı): metin yoksa `undefined`. */
  readonly dynamic: (key: string, params?: Readonly<Record<string, string | number>>) => string | undefined;
};

/**
 * Yalnız bir ekrana özgü metinler (frontend.md "Metin kataloğu"; `features/<özellik>/copy.ts`): merkezi katalog giriş
 * paketine ve QR Ekranı girişine girer, ekran metni ise feature paketiyle tembel yüklenir. Anahtarlar ve `{yer tutucu}`
 * parametreleri `t` ile aynı kurallarla derlemede denetlenir; sayılar binlik ayraçla yazılır.
 */
export function defineCopy<const M extends object>(catalog: M): ScreenCopy<M> {
  const find = (key: string): string | undefined => {
    let node: unknown = catalog;
    for (const part of key.split('.')) {
      if (typeof node !== 'object' || node === null || !(part in node)) {
        return undefined;
      }

      node = (node as Record<string, unknown>)[part];
    }

    return typeof node === 'string' ? node : undefined;
  };

  const text = (<K extends LeafKeys<M>>(key: K, ...args: ScreenCopyParams<M, K>): string => {
    const template = find(key);
    if (template === undefined) {
      return key;
    }

    const [params] = args as [Readonly<Record<string, string | number>>?];
    return params ? interpolate(template, params) : template;
  }) as ScreenCopy<M>;

  return Object.assign(text, {
    dynamic: (key: string, params?: Readonly<Record<string, string | number>>) => {
      const template = find(key);
      return template === undefined ? undefined : params ? interpolate(template, params) : template;
    },
  });
}
